import type { Href } from 'expo-router';

/**
 * The dev component gallery's registry — every route under `/components/*`
 * that has a preview, plus the copy the catalog screen (`/components`) needs
 * to list and search it.
 *
 * `href` is typed as expo-router's `Href`, and every entry below is written as
 * a LITERAL string (`'/components/all'`), never built from a slug
 * (`` `/components/${slug}` ``). With `experiments.typedRoutes` on, a literal
 * `Href` is checked against the actual route tree at compile time — a typo, or
 * an entry left pointing at a route someone deleted, becomes a `tsc` error
 * instead of a dead row a user can tap. A templated string defeats that check
 * silently (it still type-checks, it just stops meaning anything), so resist
 * "simplifying" this back to a slug map.
 *
 * `keywords` exist so a search finds a component by name even when the title
 * doesn't contain it — "input" surfaces `PSMobileInput`/`PSOtpInput`, "ticket"
 * surfaces `TicketTier`/`TicketListItem`.
 *
 * Keep `COMPONENT_CATALOG` sorted ALPHABETICALLY BY `title` — the catalog
 * screen renders it in array order, and an alphabetised list is the one order
 * that stays sensible with no further sorting logic. Add new entries in place,
 * not at the end.
 */
export type CatalogEntry = {
  /** Heading shown in the catalog list. */
  title: string;
  href: Href;
  /** One line describing what the route previews. */
  blurb: string;
  /** Extra search terms — component names, synonyms — beyond the title/blurb. */
  keywords: string[];
};

export const COMPONENT_CATALOG: CatalogEntry[] = [
  {
    title: 'AccessPassBanner',
    href: '/components/access-pass-banner',
    blurb:
      'The black Access Pass doorway from events/[id] — hardwired copy, so the only thing to preview is how it wears width: full page (its only real placement) vs squeezed, where the 85%-capped text column and right-anchored logo mark both have to hold their shape.',
    keywords: [
      'access pass',
      'banner',
      'doorway',
      'membership',
      'event detail',
      'cta',
      'upsell',
    ],
  },
  {
    title: 'APProfilePageUpsellBanner',
    href: '/components/ap-profile-page-upsell-banner',
    blurb:
      'The profile page\'s Access Pass pitch — brand→onyx gradient behind the wordmark, hardwired headline/body and a full-width "Know more" CTA, so width is the only state worth previewing.',
    keywords: [
      'access pass',
      'upsell',
      'banner',
      'profile',
      'membership',
      'cta',
      'know more',
      'gradient',
    ],
  },
  {
    title: 'BookedBanner',
    href: '/components/booked-banner',
    blurb:
      'The pink-wash strip on events/[id] saying you already hold a booking — title, subtitle and CTA label all come from the caller, so the states worth previewing are long vs short copy on either side.',
    keywords: [
      'booked',
      'banner',
      'booking',
      'rsvp',
      'event detail',
      'gradient',
      'cta',
      'view booking',
    ],
  },
  {
    title: 'CheckoutCart',
    href: '/components/checkout-cart',
    blurb:
      'The itemised cart box — free/paid/long-name lines, headerLabel overrides, single-line and all-free/empty edge cases; every amount arrives pre-formatted.',
    keywords: [
      'checkout',
      'cart',
      'tickets',
      'line items',
      'subtotal',
      'total',
      'booking',
      'confirm',
      'payment',
      'free',
    ],
  },
  {
    title: 'CheckoutEventHeader',
    href: '/components/checkout-event-header',
    blurb:
      'Event identity block on the payment/receipt screens — image + title with the 4-line clamp (imageUrl + title is the whole real prop surface).',
    keywords: [
      'checkout',
      'event header',
      'receipt',
      'confirmation',
      'booking',
      'payment',
      'image',
      'title',
      'clamp',
    ],
  },
  {
    title: 'ConfirmationPagePlanCard',
    href: '/components/confirmation-page-plan-card',
    blurb:
      "MembershipPlanCard's header alone for the checkout confirmation page — logo, plan description and the kind's glow/badge, no price or CTA.",
    keywords: [
      'membership',
      'plan',
      'card',
      'confirmation',
      'checkout',
      'trial',
      'access pass',
    ],
  },
  {
    title: 'DateSelector',
    href: '/components/date-selector',
    blurb:
      'Selected/unselected flip and the hasApTickets chip that flattens the cell’s corner, live-tappable like the event page’s date row.',
    keywords: [
      'date selector',
      'date picker',
      'day cell',
      'calendar',
      'event booking',
      'slot',
      'ap tickets',
      'access pass',
      'carousel',
    ],
  },
  {
    title: 'EventCard',
    href: '/components/event-card',
    blurb:
      "The home carousels' large event tile — horizontal scroll row, the 2-column 'All experiences' grid, and every formatSlotSummary date-label shape (today/tomorrow, ranges, month/year boundaries, next year, past).",
    keywords: [
      'event card',
      'event tile',
      'home',
      'carousel',
      'grid',
      'all experiences',
      'access pass',
      'sold out',
      'in past',
      'slot summary',
      'date label',
      'formatSlotSummary',
    ],
  },
  {
    title: 'EventDetailMeta',
    href: '/components/event-detail-meta',
    blurb:
      'Event-detail title/date/venue card — single vs multiple bookable slots, relative vs absolute dates, no-run-left state, long title/venue wraps.',
    keywords: ['event', 'meta', 'date', 'venue', 'slot', 'card', 'detail'],
  },
  {
    title: 'EventLocation',
    href: '/components/event-location',
    blurb:
      'Pin icon + venue-name link row reused across EventDetailMeta, CheckoutEventHeader, TicketListItem, and events/[id]/confirm — short vs long venue name, no wrap.',
    keywords: [
      'event location',
      'venue',
      'venue name',
      'google maps',
      'location',
      'pin',
      'link',
    ],
  },
  {
    title: 'ExternalTicketTier',
    href: '/components/external-ticket-tier',
    blurb:
      'The Ticket section an EXTERNAL event shows instead of a tier list — price_from over a raised "Recommended by Putting Scene" pitch and a full-width link out. Two props, no states, so the preview varies the one thing that does: the server\'s display string.',
    keywords: [
      'external',
      'external event',
      'ticket',
      'ticket tier',
      'get tickets',
      'price from',
      'event detail',
      'link out',
      'recommended',
    ],
  },
  {
    title: 'ImageCarousel',
    href: '/components/image-carousel',
    blurb:
      "The event page's hero — single vs multi-image (dots + web arrows), and the isInPast/isSoldOut badge+grayscale pairing exactly as events/[id] drives them.",
    keywords: [
      'image carousel',
      'carousel',
      'hero',
      'gallery',
      'pagination',
      'dots',
      'swipe',
      'event detail',
      'sold out',
      'missed out',
      'badge',
      'grayscale',
    ],
  },
  {
    title: 'MembershipCard',
    href: '/components/membership-card',
    blurb:
      'The held-membership card on profile, my-memberships and its detail page — brand→onyx gradient (current), flat onyx (upcoming) or flat grey (past), faded when cancelled.',
    keywords: [
      'membership',
      'card',
      'my-memberships',
      'my memberships',
      'detail',
      'profile',
      'held membership',
      'sectionOf',
      'gradient',
      'tone',
    ],
  },
  {
    title: 'MembershipPlanCard',
    href: '/components/membership-plan-card',
    blurb:
      'A single plan card — the withFeatures perk rows and long-copy wrap.',
    keywords: [
      'membership',
      'plan',
      'card',
      'access pass',
      'upsell',
      'features',
      'pricing',
    ],
  },
  {
    title: 'MembershipUpsellSection',
    href: '/components/membership-upsell-section',
    blurb:
      'Every deriveMembershipUpsell state, drawn live — signed out, trial graduate (ran out / perks used up), current holder, cancelled/lapsing, off-catalog plan, top-of-catalog (renders null), and long copy.',
    keywords: [
      'membership',
      'upsell',
      'access pass',
      'plan',
      'upgrade',
      'renew',
      'already used',
      'trial',
      'cancelled',
      'home',
      'memberships',
    ],
  },
  {
    title: 'PSBadge',
    href: '/components/ps-badge',
    blurb:
      'Small pill-tag label — four variants (brand/neutral/outline/trial), a large size, and a disabled dim flag, used for membership gates and sold-out overlays.',
    keywords: [
      'badge',
      'tag',
      'pill',
      'label',
      'chip',
      'sold out',
      'access pass only',
      'membership tag',
    ],
  },
  {
    title: 'PSBottomBar',
    href: '/components/ps-bottom-bar',
    blurb:
      "The event page's framed, safe-area-inset footer — content-agnostic, screen-pinned as a flex sibling (not position:absolute); pinned under a scroll of filler content.",
    keywords: [
      'bottom bar',
      'footer',
      'booking bar',
      'safe area',
      'inset',
      'sticky',
      'ticketed booking bar',
      'membership purchase bar',
    ],
  },
  {
    title: 'PSButton',
    href: '/components/ps-button',
    blurb: 'Idle, disabled, and loading states, plus a live tap-to-fire demo.',
    keywords: [
      'button',
      'cta',
      'primary',
      'submit',
      'book now',
      'confirm',
      'loading',
      'spinner',
      'disabled',
    ],
  },
  {
    title: 'PSCircleButton',
    href: '/components/ps-circle-button',
    blurb:
      'Normal vs bare, filled vs unfilled, disabled, and the brand borderColor override.',
    keywords: [
      'circle button',
      'icon button',
      'round button',
      'back button',
      'share',
      'stepper',
      'close',
      'header',
    ],
  },
  {
    title: 'PSCouponCodeInput',
    href: '/components/ps-coupon-code-input',
    blurb:
      'The coupon row (event checkout, memberships) — field + Apply, with a live instance plus empty, redeeming, server error and applied (Coupon applied! / Remove) states.',
    keywords: ['coupon', 'code', 'redeem', 'discount', 'checkout', 'input'],
  },
  {
    title: 'APEmptySection',
    href: '/components/ap-empty-section',
    blurb:
      'Empty state for home’s Access Pass exclusive section — cherry card with the eyes, "Nothing here right now :(".',
    keywords: ['empty state', 'access pass', 'exclusive', 'home', 'eyes'],
  },
  {
    title: 'PSHomeSectionEmpty',
    href: '/components/ps-home-section-empty',
    blurb:
      'Successful-but-empty section fallback — sleeping cat, light vs isBackgroundDark, with a live "Refresh" tap proving onRefresh fires (home’s Editorial Picks, memberships’ showcase/all-plans).',
    keywords: [
      'empty state',
      'empty section',
      'no results',
      'no data',
      'refresh',
      'pull to refresh',
      'home',
      'editorial picks',
      'access pass',
      'memberships',
      'showcase',
      'all plans',
      'sleeping cat',
    ],
  },
  {
    title: 'PSHomeSectionError',
    href: '/components/ps-home-section-error',
    blurb:
      'What a home/memberships carousel section draws in place of its cards on fetch failure — all three ApiErrorKind sentences (network/server/unknown), light vs isBackgroundDark, and a live tap-to-retry demo.',
    keywords: [
      'home section error',
      'error state',
      'fetch error',
      'retry',
      'network error',
      'server error',
      'carousel',
      'home',
      'access pass',
      'memberships',
      'api error kind',
      'no internet cat',
    ],
  },
  {
    title: 'PSInput',
    href: '/components/ps-input',
    blurb:
      'Every keyboard type (text/email/code/url), error + disabled states, and the validate-on-blur/clear-on-focus rhythm real screens drive it with.',
    keywords: [
      'input',
      'text field',
      'form field',
      'email',
      'coupon',
      'code',
      'url',
      'social link',
      'error',
      'validation',
      'disabled',
      'keyboard',
    ],
  },
  {
    title: 'PSLinkButton',
    href: '/components/ps-link-button',
    blurb:
      'Brand/neutral variants, disabled, a live resend-cooldown demo, and inline-in-sentence composition.',
    keywords: [
      'link',
      'link button',
      'resend',
      'retry',
      'try again',
      'cta',
      'text button',
      'underline',
      'disabled',
      'auth',
    ],
  },
  {
    title: 'PSMobileInput',
    href: '/components/ps-mobile-input',
    blurb:
      'The Indian mobile-number field — live/error/disabled, with the +91 prefix it prepends made visible; digit-capping and bulk-paste-keeps-last-10 behavior.',
    keywords: [
      'mobile',
      'phone',
      'number',
      'input',
      'field',
      'dial code',
      '+91',
      'auth',
      'otp',
    ],
  },
  {
    title: 'PSModal',
    href: '/components/ps-modal',
    blurb:
      'The undismissable blocking notice — title, reason, one CTA — opened live, with real and long copy.',
    keywords: ['modal', 'dialog', 'alert', 'popup', 'blocking', 'purchase'],
  },
  {
    title: 'PSOtpInput',
    href: '/components/ps-otp-input',
    blurb:
      "A live 6-digit field showing dual error ownership (screen vs atom), disabled, and autoFocus — auth's verify-mobile/verify-email OTP entry.",
    keywords: [
      'otp',
      'otp input',
      'input',
      'verification code',
      'one time code',
      'sms',
      'autofill',
      'verify mobile',
      'verify email',
      'auth',
    ],
  },
  {
    title: 'PSRichText',
    href: '/components/ps-rich-text',
    blurb:
      'Stored markdown rendered as tokened native text — bold/italic/both, lists, the full heading scale, links, surface="dark", and the empty-markdown no-crash guard.',
    keywords: [
      'rich text',
      'markdown',
      'description',
      'terms and conditions',
      'whats included',
      'faq',
      'render html',
      'marked',
      'links',
      'headings',
      'lists',
    ],
  },
  {
    title: 'PSText',
    href: '/components/ps-text',
    blurb:
      'Every type role, colour as a call-site prop, and the weight/size overrides real screens lean on.',
    keywords: [
      'text',
      'typography',
      'type',
      'role',
      'heading',
      'title',
      'body',
      'tags',
      'font',
    ],
  },
  {
    title: 'PSUpsellBanner',
    href: '/components/ps-upsell-banner',
    blurb:
      'The Access Pass showreel hero — live autoplaying muted/looping video with tap-to-mute and a CTA overlay, home’s real "Check out Access Pass" copy plus a long-label wrap check.',
    keywords: [
      'upsell',
      'upsell banner',
      'access pass',
      'showreel',
      'video',
      'reel',
      'autoplay',
      'mute',
      'cta',
      'home',
    ],
  },
  {
    title: 'SecretMenuCard',
    href: '/components/secret-menu-card',
    blurb:
      'Access-Pass "secret menu" tile — 3:4 poster + panel, varying on a single available / dropping-soon / sold-out state union.',
    keywords: [
      'secret menu',
      'card',
      'access pass',
      'carousel',
      'poster',
      'badge',
    ],
  },
  {
    title: 'SettingsGroup',
    href: '/components/settings-group',
    blurb:
      'Vertical group of connected, clickable rows on a recessed surface — the settings-page card (icon, label, chevron, hairline dividers, rounded outer corners).',
    keywords: [
      'settings',
      'group',
      'list',
      'row',
      'card',
      'menu',
      'account',
      'destructive',
      'chevron',
    ],
  },
  {
    title: 'Tabs',
    href: '/components/tabs',
    blurb:
      'Segmented-control filter — equal-width segments, one always selected.',
    keywords: ['tabs', 'segmented control', 'filter', 'toggle', 'switch'],
  },
  {
    title: 'TicketListItem',
    href: '/components/ticket-list-item',
    blurb:
      "A booking row from profile's upcoming/past lists and pending-requests — live tappable demo, the inert (no-onPress) delete-account shape, location/title clamps, missing-image fallback, and past's per-section dim.",
    keywords: [
      'ticket list item',
      'reservation list item',
      'booking',
      'bookings',
      'profile',
      'past bookings',
      'upcoming bookings',
      'pending request',
      'delete account',
      'ticket count',
    ],
  },
  {
    title: 'TicketTier',
    href: '/components/ticket-tier',
    blurb:
      'One purchasable ticket option from the event page — the server-sent bookability verdict drives everything: stepper vs "+ Add" vs "Sold out" badge inline, or one of seven TierNotice reasons at the foot.',
    keywords: [
      'ticket',
      'ticket tier',
      'tickets',
      'bookability',
      'tier notice',
      'sold out',
      'membership required',
      'limit reached',
      'seats exhausted',
      'guest approval',
      'rsvp',
      'stepper',
      'event booking',
      'unauthenticated',
    ],
  },
  {
    title: 'TimeSelector',
    href: '/components/time-selector',
    blurb:
      "The event page's pill-shaped time-slot chip — selected/unselected flip plus the AP-ticket corner badge.",
    keywords: [
      'time',
      'selector',
      'chip',
      'pill',
      'slot',
      'event',
      'booking',
      'ap tickets',
    ],
  },
  {
    title: 'TrialPassSection',
    href: '/components/trial-pass-section',
    blurb:
      'Home\'s "Your trial" block for trial-pass holders — validity date, per-perk booked counts and the Upgrade to Access Pass CTA, on the dark band.',
    keywords: ['trial', 'trial pass', 'perks', 'upgrade', 'home', 'membership'],
  },
  {
    title: 'UpcomingBookingBanner',
    href: '/components/upcoming-booking-banner',
    blurb:
      'The "Upcoming booking" card — static header over a 3:4 event image, 2-line title clamp and the slot line, in light and dark variants.',
    keywords: [
      'upcoming',
      'upcoming booking',
      'booking',
      'banner',
      'home',
      'next event',
      'image',
    ],
  },
  {
    title: 'UserDetails',
    href: '/components/user-details',
    blurb:
      'The profile screen\'s "Your details" box — name/email/mobile plus the three-rung onboarding nudge ladder (complete profile → add email → verify email).',
    keywords: [
      'user details',
      'profile',
      'your details',
      'contact',
      'areDetailsMissing',
      'isEmailMissing',
      'isOnlyEmailVerificationPending',
      'socialMediaLink',
      'onboarding',
      'nudge',
    ],
  },
];

/**
 * Whole screens, drawn through their pure view (`components/pages/*`) with
 * fixture props — the route that wires the hooks is never mounted. Same
 * `CatalogEntry` rules as above, routes under `/components/pages/*`.
 */
export const PAGE_CATALOG: CatalogEntry[] = [
  {
    title: 'Membership purchase status',
    href: '/components/pages/membership-purchase-status',
    blurb:
      'The waiting room after a pass purchase — confirming, activated (renews / valid until), the socials ask, every way it ends up not confirmed, and the Trial Pass panel.',
    keywords: [
      'MembershipPurchaseStatus',
      'purchase',
      'status',
      'membership',
      'access pass',
      'trial',
      'confirming',
      'activated',
      'payment',
      'socials',
    ],
  },
];
